import { useState } from 'react';
import type { MouseEvent } from 'react';
import Reveal from '@/components/base/Reveal';
import { expertisePanels } from '@/mocks/site';

export default function Expertise() {
  const [hover, setHover] = useState(0);

  const track = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <section className="py-16 md:py-[104px] bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10 md:mb-12">
          <h2 className="lg:col-span-7 font-heading uppercase text-[36px] md:text-[48px] leading-[0.95]">
            Workshop discipline,<br className="hidden md:block" /> applied to every car.
          </h2>
          <p className="lg:col-span-4 lg:col-start-9 text-base text-foreground-700 leading-relaxed">
            Three principles shape every visit — from a routine oil service to a complex electrical fault.
          </p>
        </div>

        {/* Desktop: one connected assembly with shared diagonal seams */}
        <Reveal className="hidden lg:block">
          <div className="xp-assembly" data-hover={hover} onMouseLeave={() => setHover(0)}>
            {expertisePanels.map((p, i) => (
              <div
                key={p.title}
                tabIndex={0}
                aria-label={`${p.title}: ${p.text}`}
                className={`xp-seg xp-seg-${i + 1}`}
                onMouseMove={track}
                onMouseEnter={() => setHover(i + 1)}
                onFocus={() => setHover(i + 1)}
                onBlur={() => setHover(0)}
              >
                <span className="xp-glow" />
                <div className="xp-content">
                  <div className="flex items-center gap-3">
                    <span className="font-tech text-[15px] font-semibold tracking-[0.18em] text-primary-500">0{i + 1}</span>
                    <span className="h-px w-6 bg-background-400" />
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-600 text-base"><i className={p.icon} /></span>
                  </div>
                  <h3 className="xp-title mt-auto font-heading uppercase text-[24px] leading-tight text-foreground-950">{p.title}</h3>
                  <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{p.text}</p>
                </div>
              </div>
            ))}
            {[1, 2].map((n) => (
              <span key={n} aria-hidden="true">
                <span className={`xp-seam xp-seam-${n} xp-seam-dark`} />
                <span className={`xp-seam xp-seam-${n} xp-seam-metal`} />
                <span className={`xp-seam xp-seam-${n} xp-seam-red`} />
              </span>
            ))}
            <span aria-hidden="true" className="absolute left-0 top-0 h-[2px] w-16 bg-primary-500" />
          </div>
        </Reveal>

        {/* Tablet & mobile: contiguous stacked panels */}
        <Reveal className="lg:hidden">
          <div className="border border-background-300/90 bg-background-100">
            {expertisePanels.map((p, i) => (
              <div
                key={p.title}
                tabIndex={0}
                className="group relative p-6 md:p-7 border-t border-background-300/80 first:border-t-0 outline-none transition-colors duration-300 hover:bg-background-200/40 focus-visible:bg-background-200/40 md:grid md:grid-cols-12 md:gap-6"
              >
                <span aria-hidden="true" className="absolute left-0 top-0 h-full w-[2px] bg-primary-500 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
                <div className="flex items-center gap-3 md:col-span-2">
                  <span className="font-tech text-[15px] font-semibold tracking-[0.18em] text-primary-500">0{i + 1}</span>
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-600 md:hidden"><i className={p.icon} /></span>
                </div>
                <div className="md:col-span-10">
                  <h3 className="mt-3 md:mt-0 font-heading uppercase text-[24px] leading-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}